/**
 * Body for the "Custom subdomain" option — merged input + suffix picker
 * (hand-rolled rather than the shared Input component: the spec calls for
 * a single 8px-radius/shadow-xs field split into two visually seamless
 * halves, which Input's own internal markup isn't built to expose from
 * outside), then the primary action, then one helper line that swaps
 * content in place (hint → preview → danger) without shifting layout.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import appConfig from "../../../../../appConfig";
import Button from "../../../theme/untitled/Button/Button";
import DropdownMenu from "../../../theme/untitled/DropdownMenu/DropdownMenu";
import Icon from "../../../theme/untitled/icons/Icon";
import FieldError from "./FieldError";
import { parseGeneralError } from "./errorHelpers";
import styles from "./SubdomainOption.module.scss";

const LABEL_PATTERN = /^[a-z0-9]([a-z0-9-]{0,14}[a-z0-9])?$/;

const SubdomainOption = ({ subDomain = "", onRequestSubmit, isLoading, error }) => {
  const [subDomainText, setSubDomainText] = useState("");
  const [domain, setDomain] = useState("");

  const [initialSubDomain, initialDomain] = useMemo(() => {
    if (!subDomain) {
      return ["", appConfig.publicDomains[0]];
    }
    const parts = subDomain.split(".");

    if (parts.length >= 3) {
      return [parts[0], parts.slice(1).join(".")];
    }
    return ["", subDomain];
  }, [subDomain]);

  useEffect(() => {
    setSubDomainText(initialSubDomain);
    setDomain(initialDomain);
  }, [initialSubDomain, initialDomain]);

  const onSubDomainTextChange = useCallback((e) => {
    setSubDomainText(e.target.value.trim().toLowerCase());
  }, []);

  const onDomainSelect = useCallback((d) => () => setDomain(d), []);

  const hasExisting = initialSubDomain.length > 0;
  const isDirty = subDomainText !== initialSubDomain || domain !== initialDomain;
  const isValid = LABEL_PATTERN.test(subDomainText);
  const canSubmit = isDirty && isValid;
  const isReplacing = isDirty && hasExisting;
  const fullHost = `${subDomainText}.${domain}`;

  const _onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!canSubmit) return;
      onRequestSubmit(fullHost, isReplacing);
    },
    [canSubmit, fullHost, isReplacing, onRequestSubmit]
  );

  const dropdownItems = useMemo(
    () => appConfig.publicDomains.map((d) => ({ key: d, label: `.${d}`, onClick: onDomainSelect(d) })),
    [onDomainSelect]
  );

  const errorMessage = error
    ? parseGeneralError(error, "The service is unavailable. Try again in a moment.")
    : null;

  let helper;
  if (!isDirty) {
    helper = (
      <span className={styles.helperHint}>
        <Icon name="HelpCircle" size={14} className={styles.helperIcon} />
        1–16 characters. Letters, numbers, and hyphens only.
      </span>
    );
  } else if (isReplacing) {
    helper = (
      <span className={styles.helperDanger}>
        <Icon name="AlertTriangle" size={14} className={styles.helperIconDanger} />
        New address: <strong>{fullHost}</strong> — this replaces your current subdomain and
        breaks existing QR codes and shared links.
      </span>
    );
  } else if (isValid) {
    helper = (
      <span className={styles.helperHint}>
        <Icon name="HelpCircle" size={14} className={styles.helperIcon} />
        New address: <strong>{fullHost}</strong>
      </span>
    );
  } else {
    helper = (
      <span className={styles.helperHint}>
        <Icon name="HelpCircle" size={14} className={styles.helperIcon} />
        1–16 characters. Letters, numbers, and hyphens only.
      </span>
    );
  }

  return (
    <>
      <div className={styles.row}>
        <div className={styles.fieldGroup}>
          <input
            type="text"
            id="subdomain"
            name="subdomain"
            className={styles.groupInput}
            placeholder="your-name"
            disabled={isLoading}
            value={subDomainText}
            onChange={onSubDomainTextChange}
            aria-label="Subdomain"
          />
          <DropdownMenu
            align="end"
            trigger={
              <button type="button" className={styles.suffixTrigger} disabled={isLoading}>
                .{domain}
                <Icon name="ChevronDown" size={16} />
              </button>
            }
            items={dropdownItems}
          />
        </div>
        <Button hierarchy="primary" onClick={_onSubmit} disabled={!canSubmit || isLoading} loading={isLoading}>
          {isLoading ? "Saving…" : hasExisting ? "Update subdomain" : "Set subdomain"}
        </Button>
      </div>

      <div className={styles.helperSlot}>{helper}</div>

      <FieldError message={errorMessage} />
    </>
  );
};

export default SubdomainOption;
